import { Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useParams } from "react-router-dom";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../utils/utils";
import * as Yup from "yup";
import OutlineCard from "../../../../../components/OutlineCard";
import CustomSelect from "../../../../../components/CustomSelect";
import Input from "../../../../../components/Input";
import { useEffect } from "react";
import { useAddFaxIntegration } from "../../../../../queries/communication/fax-integration/use-add-fax-integration";
import { useUpdateFaxIntegration } from "../../../../../queries/communication/fax-integration/use-update-fax-integration";
import NumberInput from "../../../../../components/NumberInput";
import PasswordInput from "../../../../../components/app/PasswordInput";

interface FaxIntegrationFormValues {
  uuid?: string;
  vendor: string | undefined;
  userName?: string;
  password?: string;
  senderEmail?: string;
  faxNumber?: string;
  archive?: boolean;
}

const FAX_INTEGRATION_INITIAL_VALUES: FaxIntegrationFormValues = {
  uuid: undefined,
  vendor: "SRFAX",
  userName: undefined,
  password: undefined,
  senderEmail: undefined,
  faxNumber: undefined,
  archive: false,
};

const FAX_INTEGRATION_VALIDATION = Yup.object().shape({
  vendor: Yup.string().required("Vendor Type is required"),
  userName: Yup.string().required("User Name is required"),
  senderEmail: Yup.string().required("Sender Email is required"),
  faxNumber: Yup.number().required("Fax Number is required"),
  archive: Yup.boolean().optional(),
  password: Yup.string().optional(),
});

interface UpsertFaxIntegrationDrowerProps {
  disClosureHost: DisclosureHost;
  title?: string;
}

const UpsertFaxIntegrationDrower = ({
  disClosureHost,
  title,
}: UpsertFaxIntegrationDrowerProps) => {
  const prefillData = disClosureHost.data as FaxIntegrationFormValues;
  const params = useParams();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const { mutateAsync: addFaxIntegration } = useAddFaxIntegration();
  const { mutateAsync: updateFaxIntegration } = useUpdateFaxIntegration();
  const formik = useFormik<FaxIntegrationFormValues>({
    initialValues: FAX_INTEGRATION_INITIAL_VALUES,
    validationSchema: FAX_INTEGRATION_VALIDATION,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  async function handleSubmit(values: FaxIntegrationFormValues) {
    const payload = {
      faxIntegration: values,
      xTENANTID: getXtenantId() ?? params?.providerGroupId ?? undefined,
    };
    showToastAndReset(
      prefillData
        ? updateFaxIntegration(payload as any)
        : addFaxIntegration(payload as any)
    );
  }

  const showToastAndReset = (apiCall: Promise<any>) => {
    apiCall
      ?.then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({
          queryKey: ["get-fax-integration-list"],
        });
        formik.resetForm();
        disClosureHost.close();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  // this flow comes from Edit fax integration modal
  useEffect(() => {
    if (prefillData) {
      formik.setValues({
        uuid: prefillData.uuid,
        vendor: prefillData.vendor,
        userName: prefillData.userName,
        password: prefillData.password,
        senderEmail: prefillData.senderEmail,
        faxNumber: prefillData.faxNumber,
        archive: false,
      });
    }
  }, [prefillData]);

  const handleSelectVendorType = (value: string | null) => {
    // formik.setFieldValue("sftpHost", value ? SFTP_HOST_PREFILL[value] : "");

    formik.setFieldValue("vendor", value, true);
  };

  return (
    <SideSheetDrawer
      open={disClosureHost.opened}
      w="44vw"
      title={title}
      onClose={() => disClosureHost.close()}
      footerActions={
        <>
          <Button variant="outlined" onClick={() => disClosureHost.close()}>
            Cancel
          </Button>
          <Button
            onClick={() => formik.handleSubmit()}
            sx={{ width: "6rem" }}
            variant="contained"
          >
            Save
          </Button>
        </>
      }
    >
      <Grid width={"100%"} container spacing={3}>
        {/* Claim Configuration Section*/}
        <Grid item xs={12}>
          <OutlineCard>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CustomSelect
                  label="Vendor Type"
                  required
                  readOnly={true}
                  name="vendor"
                  placeholder="Select Vendor Type"
                  options={[
                    {
                      value: "SRFAX",
                      label: "SRFAX",
                    },
                  ]}
                  value={formik.values.vendor}
                  onChange={handleSelectVendorType}
                  error={formik.touched.vendor && formik.errors.vendor}
                />
              </Grid>
              <Grid item xs={12}>
                <Input
                  label="User Name"
                  required
                  textFieldProps={{
                    placeholder: "Enter User Name",
                    name: "userName",
                    value: formik.values.userName,
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                  }}
                  error={formik.touched.userName && formik.errors.userName}
                />
              </Grid>
              <Grid item xs={12}>
                <Input
                  label="Sender Email"
                  required
                  textFieldProps={{
                    placeholder: "Enter Sender Email",
                    name: "senderEmail",
                    value: formik.values.senderEmail,
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                  }}
                  error={
                    formik.touched.senderEmail && formik.errors.senderEmail
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <NumberInput
                  label="Fax Number"
                  textFieldProps={{
                    placeholder: "Enter Fax Number",
                    name: "faxNumber",
                    value: formik.values.faxNumber,
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                  }}
                  error={formik.touched.faxNumber && formik.errors.faxNumber}
                />
              </Grid>
              <Grid item xs={12}>
                <PasswordInput
                  label="Password"
                  required
                  placeholder="Enter Password"
                  name="password"
                  value={formik.values.password}
                  onChange={(v) => formik.setFieldValue("password", v)}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && formik.errors.password}
                />
              </Grid>
            </Grid>
          </OutlineCard>
        </Grid>
      </Grid>
    </SideSheetDrawer>
  );
};

export default UpsertFaxIntegrationDrower;
